import { http } from '@/api/core/request';
import { ApiUrls } from '@/api/endpoints/apiUrls';
import { CryptoUtils } from '@/utils/crypto-utils';
import {
  UserRegisterParams,
  UserLoginParams,
  UserAuthResult,
  UserUpdateParams,
  UserInfo,
  SendCodeParams,
  ResetPasswordParams,
  EncryptKeyResult,
  UploadContactListParams,
  UserTenantPermissions,
  UserPresence,
  MediaUploadParams,
  MediaUploadResult,
  MediaDownloadParams,
  MediaDownloadResult,
  QrCodeResult,
  QrCodeVerifyResult,
  CheckDomainParams,
  CheckDomainResult
} from '@/api/types';
import { logPrint } from '@/utils/my-utils';

/**
 * 用户服务
 * 处理用户相关的API请求
 */
export class UserService {

  /**
  * 检查域名可用性
  * @param params 检查参数
  * @returns 检查结果
  */
  static async checkDomain(params: CheckDomainParams): Promise<CheckDomainResult> {
    return http.get<CheckDomainResult>(ApiUrls.USER.CHECK_DOMAIN, params, {
    });
  }

  /**
   * 用户注册（原始方法，不建议直接使用）
   * @param params 注册参数
   * @returns 认证结果
   */
  static async registerRaw(params: UserRegisterParams): Promise<UserAuthResult> {
    return http.post<UserAuthResult>(ApiUrls.USER.REGISTER, params, {
    });
  }

  /**
   * 用户注册（推荐使用，自动处理密码加密）
   * @param params 注册参数
   * @returns 认证结果
   */
  static async register(params: UserRegisterParams): Promise<UserAuthResult> {
    try {
      // 1. 获取加密公钥
      const encryptKeyResult = await UserService.getEncryptKey();
      logPrint('获取到公钥用于注册:', encryptKeyResult.public_key);

      // 2. 加密密码
      const encryptedPassword = await CryptoUtils.encryptPassword(params.password, encryptKeyResult.public_key);
      logPrint('注册密码加密成功');

      // 3. 构造加密后的注册参数
      const encryptedParams: UserRegisterParams = {
        ...params,
        password: encryptedPassword
      };

      // 4. 发送注册请求
      return await UserService.registerRaw(encryptedParams);
    } catch (error) {
      console.error('注册过程中发生错误:', error);
      throw error;
    }
  }

  /**
   * 用户登录（原始方法，不建议直接使用）
   * @param params 登录参数
   * @returns 认证结果
   */
  static async loginRaw(params: UserLoginParams): Promise<UserAuthResult> {
    return http.post<UserAuthResult>(ApiUrls.USER.LOGIN, params, {
    });
  }

  /**
   * 用户登录（推荐使用，自动处理密码加密）
   * @param params 登录参数
   * @returns 认证结果
   */
  static async login(params: UserLoginParams): Promise<UserAuthResult> {
    try {
      // 1. 获取加密公钥
      const encryptKeyResult = await UserService.getEncryptKey();
      logPrint('获取到公钥:', encryptKeyResult.public_key);

      // 2. 加密密码
      const encryptedPassword = await CryptoUtils.encryptPassword(params.password, encryptKeyResult.public_key);
      logPrint('密码加密成功');

      // 3. 构造加密后的登录参数
      const encryptedParams: UserLoginParams = {
        ...params,
        password: encryptedPassword
      };

      // 4. 发送加密登录请求
      return await UserService.loginRaw(encryptedParams);
    } catch (error) {
      console.error('登录过程中发生错误:', error);
      throw error;
    }
  }

  /**
   * 用户登出
   * @returns 空响应
   */
  static async logout(): Promise<void> {
    return http.get<void>(ApiUrls.USER.LOGOUT);
  }

  /**
   * 更新用户信息
   * @param params 更新参数
   * @returns 更新后的用户信息
   */
  static async updateUserInfo(params: UserUpdateParams): Promise<UserInfo> {
    return http.post<UserInfo>(ApiUrls.USER.UPDATE, params);
  }

  /**
   * 获取用户信息
   * @param q 搜索查询（用户ID、handle或昵称）
   * @returns 用户信息
   */
  static async getUserInfo(q?: string): Promise<UserInfo> {
    return http.get<UserInfo>(ApiUrls.USER.INFO, { q });
  }

  /**
   * 获取用户租户权限
   * @returns 用户租户权限信息
   */
  static async getUserTenantPermissions(): Promise<UserTenantPermissions> {
    return http.get<UserTenantPermissions>(ApiUrls.USER.TENANT_PERMISSIONS);
  }

  /**
   * 获取密码加密密钥
   * @returns 公钥信息
   */
  static async getEncryptKey(): Promise<EncryptKeyResult> {
    return http.get<EncryptKeyResult>(ApiUrls.USER.GET_ENCRYPT_KEY, {}, {
    });
  }

  /**
   * 发送验证码
   * @param params 发送参数
   * @returns 空响应
   */
  static async sendCode(params: SendCodeParams): Promise<void> {
    return http.post<void>(ApiUrls.USER.SEND_CODE, params, {
    });
  }

  // 暂时不用这个接口
  // /**
  //  * 搜索用户
  //  * @param params 搜索参数
  //  * @returns 搜索结果
  //  */
  // static async searchUsers(params: SearchParams): Promise<UserSearchResult> {
  //   return http.get<UserSearchResult>(ApiUrls.USER.SEARCH, params);
  // }

  /**
   * 获取媒体上传URL
   * @param params 上传参数
   * @returns 上传URL和媒体ID
   */
  static async getUploadUrl(params: MediaUploadParams): Promise<MediaUploadResult> {
    return http.post<MediaUploadResult>(ApiUrls.USER.UPLOAD_URL, params);
  }

  /**
   * 获取媒体下载URL
   * @param params 下载参数
   * @returns 下载URL
   */
  static async getDownloadUrl(params: MediaDownloadParams): Promise<MediaDownloadResult> {
    return http.post<MediaDownloadResult>(ApiUrls.USER.DOWNLOAD_URL, params);
  }

  /**
   * 获取二维码
   * @param type 二维码类型
   * @returns 二维码信息
   */
  static async getQrCode(type?: 'user_profile' | 'group_invite'): Promise<QrCodeResult> {
    return http.get<QrCodeResult>(ApiUrls.USER.QR_CODE_GET, { type });
  }

  /**
   * 刷新二维码
   * @param type 二维码类型
   * @returns 新的二维码信息
   */
  static async refreshQrCode(type?: 'user_profile' | 'group_invite'): Promise<QrCodeResult> {
    return http.get<QrCodeResult>(ApiUrls.USER.QR_CODE_REFRESH, { type });
  }

  /**
   * 验证二维码
   * @param qrId 二维码ID
   * @returns 验证结果
   */
  static async verifyQrCode(qrId: string): Promise<QrCodeVerifyResult> {
    const url = ApiUrls.getUrl(ApiUrls.USER.QR_CODE_VERIFY, { qr_id: qrId });
    return http.get<QrCodeVerifyResult>(url);
  }

  /**
   * 重置密码（原始方法，不建议直接使用）
   * @param params 重置参数
   * @returns 空响应
   */
  static async resetPasswordRaw(params: ResetPasswordParams): Promise<void> {
    return http.post<void>(ApiUrls.USER.RESET_PASSWORD, params, {
    });
  }

  /**
   * 重置密码（推荐使用，自动处理密码加密）
   * @param params 重置参数
   * @returns 空响应
   */
  static async resetPassword(params: ResetPasswordParams): Promise<void> {
    try {
      // 1. 获取加密公钥
      const encryptKeyResult = await UserService.getEncryptKey();
      logPrint('获取到公钥用于重置密码:', encryptKeyResult.public_key);

      // 2. 加密新密码
      const encryptedPassword = await CryptoUtils.encryptPassword(params.password, encryptKeyResult.public_key);
      logPrint('重置密码加密成功');

      // 3. 构造加密后的重置参数
      const encryptedParams: ResetPasswordParams = {
        ...params,
        password: encryptedPassword
      };

      // 4. 发送重置密码请求
      return await UserService.resetPasswordRaw(encryptedParams);
    } catch (error) {
      console.error('重置密码过程中发生错误:', error);
      throw error;
    }
  }

  /**
   * 上传联系人列表
   * @param params 联系人列表参数
   * @returns 空响应
   */
  static async uploadContactList(params: UploadContactListParams): Promise<void> {
    return http.post<void>(ApiUrls.USER.UPLOAD_CONTACT_LIST, params);
  }

  /**
   * 获取好友状态
   * @returns 好友状态列表
   */
  static async getUserPresence(): Promise<UserPresence[]> {
    return http.get<UserPresence[]>(ApiUrls.USER.PRESENCE);
  }

  /**
   * 获取指定用户状态
   * @param userId 用户ID
   * @returns 用户状态
   */
  static async getUserPresenceById(userId: string): Promise<UserPresence[]> {
    const url = ApiUrls.getUrl(ApiUrls.USER.PRESENCE_BY_ID, { user_id: userId });
    return http.get<UserPresence[]>(url);
  }
}
